import * as React from "react";
import {HistoricHand, Player, GameCard, PlayerBestHand, PlayerAction, HandResult} from "../../../../generated/protocol";
import {CardUtils} from "./CardUtils";

interface PokerTableProps {
    hand: HistoricHand, width: number,
    smallBlindPlayerId: number,
    bigBlindPlayerId: number,
    dealerPlayerId: number,
}

export const PokerTable = (props: PokerTableProps): JSX.Element => {
    return (
        <div className={"hand-history-table capacity-" + props.hand.settings.capacity}
             style={{fontSize: 100 * (props.width / 650) + "%"}}>
            <Seats
                capacity={props.hand.settings.capacity}
                hand={props.hand}
                bigBlindPlayerId={props.bigBlindPlayerId}
                smallBlindPlayerId={props.smallBlindPlayerId}
                dealerPlayerId={props.dealerPlayerId}
            />
            <TableInfo hand={props.hand}/>
            <div className="table-name">{props.hand.table.tableName}</div>
            <CommunityCards hand={props.hand}/>
        </div>
    )
}
export const TableInfo = (props: { hand: HistoricHand }) => {
    return (
        <div className="table-info">
            <div className="total-pot pull-right">
                <span className="table-info-label">Pot:</span> {CardUtils.calculateTotalPot(props.hand.results)}
            </div>
            <div className="blinds">
                {props.hand.settings.ante > 0 &&
                <span> <span className="table-info-label">Ante:</span> {props.hand.settings.ante}</span>}
                <span><span
                    className="table-info-label">Blinds:</span> {props.hand.settings.smallBlind + "/" + props.hand.settings.smallBlind}</span>
            </div>

        </div>
    );
}

export const CommunityCards = (props: { hand: HistoricHand }) => {
    let gameCards: GameCard[] = CardUtils.getCommunityCardsFromHand(props.hand);
    return (
        <div className="community-cards">
            {gameCards.map((c, i) => <CommunityCard key={c.id} card={c} index={i}/>)}
        </div>
    );
}
export const CommunityCard = (props: { card: GameCard, index: number }) => {
    return (
        <div className={"community-card community-card-" + props.index}>
            <CardImage card={props.card}/>
        </div>
    )
}


export function seatById(id: number, seats: Player[]): Player {
    return seats.find(s => id == s.seatId);
}

interface SeatsProps {
    hand: HistoricHand,
    capacity: number,
    smallBlindPlayerId: number,
    bigBlindPlayerId: number,
    dealerPlayerId: number,
}

export const Seats = (props: SeatsProps): any => {
    let seats = [];

    for (var i = 0; i < props.capacity; i++) {

        let playerSeat = seatById(i, props.hand.seats);
        let cardsFromSeat = CardUtils.getCardsFromSeat(props.hand, playerSeat);
        let bestHand = CardUtils.getBestHandForPlayer(props.hand, playerSeat && playerSeat.playerId)
        let highlight = CardUtils.isHighlightSeat(props.hand, playerSeat && playerSeat.playerId)
        let lastAction: PlayerAction = CardUtils.getLastActionForPlayer(props.hand, playerSeat && playerSeat.playerId);
        let result = CardUtils.getResultsForPlayer(props.hand.results, playerSeat && playerSeat.playerId)
        seats.push(
            <Seat
                key={i}
                id={i}
                player={playerSeat}
                cards={cardsFromSeat}
                bestHand={bestHand}
                highlight={highlight}
                lastAction={lastAction}
                result={result}
                isSmallBlind={playerSeat && playerSeat.playerId == props.smallBlindPlayerId}
                isBigBlind={playerSeat && playerSeat.playerId == props.bigBlindPlayerId}
                isDealer={playerSeat && playerSeat.playerId == props.dealerPlayerId}
            />
        );

    }

    return <div className="seat-container">{seats}</div>;
}

interface PlayerSeatProps {
    id?: number,
    player?: Player,
    cards?: GameCard[],
    bestHand?: PlayerBestHand,
    highlight?: boolean,
    lastAction?: PlayerAction;
    result?: HandResult;
    isSmallBlind?: boolean;
    isBigBlind?: boolean;
    isDealer?: boolean;
};
export const Seat = (props: PlayerSeatProps): JSX.Element => {
    if (!props.player) {
        return <div className={"empty-seat seat-" + props.id}>
            <div className="player-seat-info"></div>
        </div>
    } else {
        return <PlayerSeat {...props}/>
    }
}

export const PlayerSeat = (props: PlayerSeatProps): JSX.Element => {
    const marker = (() => {
        switch (true) {
            case props.isDealer:
                return <div className={"marker dealer"}>D</div>;
            case props.isSmallBlind:
                return <div className={"marker"}>SB</div>;
            case props.isBigBlind:
                return <div className={"marker"}>BB</div>;
            default:
                return null;
        }
    })();
    return (
        <div className={"player-seat seat-" + props.id + (props.highlight ? " highlight" : "")}>
            <div className="player-seat-info">
                <div className="player-name"> {props.player.name}</div>
                <div className="player-final-balance">{props.player.initialBalance}</div>
            </div>
            {props.lastAction &&
            <div className="player-action">
                {props.lastAction.action} {props.lastAction.timeout &&
            <span className="fa fa-clock-o " style={{color: "rgb(255, 69, 69)"}}></span>}
            </div>
            }
            {props.result &&
            <div className="player-result">
                {props.result.netWin > 0 && <span className="won">{"+" + props.result.netWin} </span>}
                {props.result.netWin < 0 && <span className="lost">{props.result.netWin} </span>}
            </div>
            }
            <PocketCards cards={props.cards}/>
            { marker }
        </div>
    );
}

export const PocketCards = (props: { cards: GameCard[] }): any => {
    return (
        <div className="pocket-cards">
            {props.cards.map((card, i) => <PocketCard key={card.id} totalCount={props.cards.length} index={i}
                                                      card={card}/>)}
        </div>
    );
}

export function calculateCardStyle(index: number, totalCount: number) {
    let totalWidth = 100;
    let cardWidth = 30;
    let cardHeight = 2.25 * cardWidth;
    let widthOfAllCards = cardWidth + ((cardWidth / 2) * (totalCount - 1));
    var remainingWidth = totalWidth - widthOfAllCards;
    var startOfFirstCard = remainingWidth / 2;
    let left = startOfFirstCard + (index * (cardWidth / 2));

    return {left: left + "%", width: cardWidth + "%", height: cardHeight + "%"};

}

export const PocketCard = (props: { card: GameCard, index: number, totalCount: number }): any => {
    let {index, totalCount} = props;
    return (
        <div className={"pocket-card card-" + index + "-of-" + totalCount}
             style={calculateCardStyle(index, totalCount)}>
            <CardImage card={props.card}/>
        </div>
    );
}
export const CardImage = (props: { card: GameCard }) => {
    return <img src={contextPath + "/images/poker/cards/" + CardUtils.getCardString(props.card) + ".svg"}/>;
}




// WEBPACK FOOTER //
// ./src/app/ui/poker/history/PokerTable.tsx